import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import Layout from "../components/Layout";
import { PayPalCheckout } from "@/components/PayPalCheckout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CreditCard, DollarSign, Smartphone } from "lucide-react";
import { usePaymentMethods } from "@/hooks/usePaymentMethods";
import { useTourBookings } from "@/hooks/useTourBookings";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface BookingData {
  tourName: string;
  tourImage: string;
  selectedDate: string;
  adults: number;
  selectedTime: string;
  totalPrice: number;
  pricePerPerson: number;
}

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const bookingData = location.state as BookingData;
  const { paymentMethods, loading: paymentLoading, getMethodTitle, getMethodDescription } = usePaymentMethods();
  const { createTourBooking } = useTourBookings();

  const [customerData, setCustomerData] = useState({
    fullName: "",
    email: "",
    pickupPoint: "",
    mobilePhone: ""
  });

  const [selectedPayment, setSelectedPayment] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPayPalCheckout, setShowPayPalCheckout] = useState(false);
  const [paypalOptions, setPaypalOptions] = useState<any>(null);
  const [paypalConfigLoaded, setPaypalConfigLoaded] = useState(false);
  const [paypalConfigError, setPaypalConfigError] = useState<string | null>(null);
  const [currentBooking, setCurrentBooking] = useState<any>(null);

  if (!bookingData) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">No booking data found</h1>
            <Button onClick={() => navigate("/tours")}>Back to Tours</Button>
          </div>
        </div>
      </Layout>
    );
  }

  // Load PayPal configuration when PayPal is selected
  const loadPayPalConfig = async () => {
    try {
      setPaypalConfigError(null);
      console.log('Loading PayPal configuration...');
      
      const { data, error } = await supabase.functions.invoke('get-paypal-client-id');
      
      if (error) {
        throw new Error(`Failed to get PayPal config: ${error.message}`);
      }

      if (!data.success || !data.client_id) {
        throw new Error(data.error || 'PayPal not configured');
      }

      const options = {
        clientId: data.client_id,
        currency: "USD",
        intent: "capture" as const,
        "enable-funding": "card,venmo,paylater",
        "disable-funding": "",
        components: "buttons,marks",
        locale: "en_US"
      };

      console.log('PayPal configuration loaded successfully');
      setPaypalOptions(options);
      setPaypalConfigLoaded(true);
      return true;
    } catch (err: any) {
      console.error('Error loading PayPal config:', err);
      setPaypalConfigError(err.message || 'Failed to load PayPal configuration');
      return false;
    }
  };

  // Load PayPal config when PayPal is selected as payment method
  useEffect(() => {
    if (selectedPayment === 'paypal' && !paypalOptions && !paypalConfigLoaded && !paypalConfigError) {
      console.log('Loading PayPal configuration for selected payment method...');
      loadPayPalConfig();
    }
  }, [selectedPayment, paypalOptions, paypalConfigLoaded, paypalConfigError]);

  const handleInputChange = (field: string, value: string) => {
    setCustomerData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePayment = async () => {
    if (!selectedPayment) {
      toast({
        title: "Error",
        description: "Por favor selecciona un método de pago",
        variant: "destructive",
      });
      return;
    }

    if (!customerData.fullName || !customerData.email || !customerData.pickupPoint || !customerData.mobilePhone) {
      toast({
        title: "Error",
        description: "Por favor completa todos los campos requeridos",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsProcessing(true);

      // Prepare tour booking data
      const tourBookingData = {
        tour_name: bookingData.tourName,
        customer_name: customerData.fullName,
        email: customerData.email,
        phone: customerData.mobilePhone,
        pickup_point: customerData.pickupPoint,
        booking_date: bookingData.selectedDate,
        tour_schedule: bookingData.selectedTime,
        adults: bookingData.adults,
        total_price: bookingData.totalPrice,
        payment_method: selectedPayment,
        payment_status: 'pending' as const,
        booking_status: 'pending' as const,
      };

      // Create the tour booking
      const newBooking = await createTourBooking(tourBookingData);
      setCurrentBooking(newBooking);

      // Handle PayPal payment
      if (selectedPayment === 'paypal') {
        // Show embedded PayPal checkout
        setShowPayPalCheckout(true);
        toast({
          title: "Booking Created",
          description: "Please complete your payment below.",
        });
        return;
      }

      // For other payment methods (like cash)
      toast({
        title: "¡Reserva Confirmada!",
        description: "Tu reserva ha sido creada exitosamente",
      });

      // Navigate to confirmation page with booking details
      navigate("/tour-booking-confirmation", {
        state: {
          booking_number: newBooking.booking_number,
          tour_name: newBooking.tour_name,
          customer_name: newBooking.customer_name,
          email: newBooking.email,
          booking_date: newBooking.booking_date,
          tour_schedule: newBooking.tour_schedule,
          adults: newBooking.adults,
          total_price: newBooking.total_price,
          payment_method: newBooking.payment_method,
        }
      });
    } catch (error) {
      console.error('Error creating tour booking:', error);
      toast({
        title: "Error",
        description: "No se pudo crear la reserva. Por favor intenta de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-center mb-2">Checkout</h1>
            <p className="text-muted-foreground text-center">Complete your booking details</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Left Side - Customer Form & Payment */}
            <div className="space-y-6">
              <Card className="shadow-lg border-0 bg-white/80 backdrop-blur">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">1</span>
                    Customer Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible defaultValue="customer-info">
                    <AccordionItem value="customer-info">
                      <AccordionTrigger className="text-left">
                        Personal Details
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 pt-4">
                        <div>
                          <Label htmlFor="fullName">Full Name *</Label>
                          <Input
                            id="fullName"
                            placeholder="Enter your full name"
                            value={customerData.fullName}
                            onChange={(e) => handleInputChange("fullName", e.target.value)}
                            className="mt-1"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            type="email"
                            placeholder="Enter your email"
                            value={customerData.email}
                            onChange={(e) => handleInputChange("email", e.target.value)}
                            className="mt-1"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="pickupPoint">Pickup Point *</Label>
                          <Textarea
                            id="pickupPoint"
                            placeholder="Enter your hotel name or pickup address"
                            value={customerData.pickupPoint}
                            onChange={(e) => handleInputChange("pickupPoint", e.target.value)}
                            className="mt-1"
                            rows={3}
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="mobilePhone">Mobile Phone Number *</Label>
                          <Input
                            id="mobilePhone"
                            type="tel"
                            placeholder="+1 (555) 000-0000"
                            value={customerData.mobilePhone}
                            onChange={(e) => handleInputChange("mobilePhone", e.target.value)}
                            className="mt-1"
                          />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>

              {/* Payment Methods */}
              <Card className="shadow-lg border-0 bg-white/80 backdrop-blur">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">2</span>
                    Payment Method
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {paymentLoading ? (
                    <div className="space-y-4">
                      <div className="h-16 bg-gray-100 rounded-lg animate-pulse" />
                      <div className="h-16 bg-gray-100 rounded-lg animate-pulse" />
                      <div className="h-16 bg-gray-100 rounded-lg animate-pulse" />
                    </div>
                  ) : paymentMethods.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground">No hay métodos de pago activos disponibles</p>
                    </div>
                  ) : (
                    paymentMethods.map((method) => {
                      const Icon = method.method_type === 'cash' ? DollarSign :
                                  method.method_type === 'paypal' ? Smartphone :
                                  CreditCard;
                      
                      return (
                        <div
                          key={method.id}
                          className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                            selectedPayment === method.method_type ? "border-primary bg-primary/5" : "border-gray-200"
                          }`}
                          onClick={() => setSelectedPayment(method.method_type)}
                        >
                          <div className="flex items-center gap-3">
                            {method.method_type === 'paypal' ? (
                              <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center">
                                <span className="text-white font-bold text-xs">PP</span>
                              </div>
                            ) : (
                              <Icon className={`h-8 w-8 ${
                                method.method_type === 'cash' ? 'text-green-600' :
                                method.method_type === 'stripe' ? 'text-purple-600' :
                                'text-blue-600'
                              }`} />
                            )}
                            <div>
                              <h3 className="font-semibold">{getMethodTitle(method.method_type)}</h3>
                              <p className="text-sm text-muted-foreground">{getMethodDescription(method.method_type)}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}

                  {/* Show PayPal checkout immediately when PayPal is selected */}
                  {selectedPayment === 'paypal' && (
                    <div className="mt-6 space-y-4">
                      {paypalConfigError ? (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                          <p className="text-sm">Error loading PayPal: {paypalConfigError}</p>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="mt-2" 
                            onClick={loadPayPalConfig}
                          >
                            Retry
                          </Button>
                        </div>
                      ) : !paypalOptions ? (
                        <div className="w-full p-6 bg-muted/50 rounded-lg animate-pulse">
                          <div className="h-12 bg-muted rounded mb-4"></div>
                          <div className="text-center text-muted-foreground">Loading PayPal...</div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="text-center text-sm text-muted-foreground">
                            Multiple Payment Options
                          </div>
                          <PayPalScriptProvider options={paypalOptions}>
                            <PayPalCheckout
                              booking={{
                                ...bookingData,
                                booking_number: `TEMP-${Date.now()}`, // Temporary booking number for display
                                customer_name: customerData.fullName,
                                email: customerData.email,
                                phone: customerData.mobilePhone,
                                pickup_point: customerData.pickupPoint
                              }}
                              amount={bookingData.totalPrice.toString()}
                              currency="USD"
                              onSuccess={(details) => {
                                // Handle successful payment
                                navigate("/tour-booking-confirmation", {
                                  state: {
                                    booking_number: details.booking_number,
                                    tour_name: bookingData.tourName,
                                    customer_name: customerData.fullName,
                                    email: customerData.email,
                                    booking_date: bookingData.selectedDate,
                                    tour_schedule: bookingData.selectedTime,
                                    adults: bookingData.adults,
                                    total_price: bookingData.totalPrice,
                                    payment_method: 'paypal',
                                    paymentData: details
                                  }
                                });
                              }}
                              onError={(error) => {
                                console.error('PayPal payment error:', error);
                                toast({
                                  title: "Payment Error",
                                  description: "There was an error processing your payment. Please try again.",
                                  variant: "destructive",
                                });
                              }}
                            />
                          </PayPalScriptProvider>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Show confirmation button only for non-PayPal payments */}
                  {selectedPayment !== 'paypal' && (
                    <Button 
                      className="w-full mt-6" 
                      size="lg"
                      onClick={handlePayment}
                      disabled={!selectedPayment || !customerData.fullName || !customerData.email || !customerData.pickupPoint || !customerData.mobilePhone || isProcessing}
                    >
                      {isProcessing ? "Procesando..." : "Confirmar Reserva"}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Side - Booking Summary */}
            <div className="space-y-6">
              <Card className="shadow-lg border-0 bg-white/80 backdrop-blur sticky top-4">
                <CardHeader>
                  <CardTitle>Booking Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Tour Image */}
                  <div className="aspect-video rounded-lg overflow-hidden">
                    <img 
                      src={bookingData.tourImage} 
                      alt={bookingData.tourName}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Tour Details */}
                  <div>
                    <h3 className="font-bold text-lg mb-2">{bookingData.tourName}</h3>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Date:</span>
                        <span className="font-medium">{bookingData.selectedDate}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Time:</span>
                        <span className="font-medium">{bookingData.selectedTime}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Travelers:</span>
                        <span className="font-medium">{bookingData.adults} Adult{bookingData.adults > 1 ? 's' : ''}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Price per person:</span>
                        <span className="font-medium">${bookingData.pricePerPerson}</span>
                      </div>
                    </div>
                  </div>

                  {/* Total */}
                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold">Total Amount</span>
                      <span className="text-2xl font-bold text-primary">${bookingData.totalPrice}</span>
                    </div>
                  </div>

                  {/* Booking Benefits */}
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">Your Benefits</h4>
                    <ul className="text-sm text-green-700 dark:text-green-300 space-y-1">
                      <li>✓ Free cancellation up to 24 hours</li>
                      <li>✓ Instant confirmation</li>
                      <li>✓ Mobile voucher accepted</li>
                      <li>✓ Professional guide included</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Checkout;